import { AnalyticsEventName } from '@dupe/common/constants'
import { useEffect, useState } from 'react'
import Button from '~/src/components/Button'
import OnboardingTitle from '~/src/components/onboarding/OnboardingTitle'
import useAnalytics from '~/src/hooks/useAnalytics'
import useUpdateUserMetadata from '~/src/hooks/useUpdateUserMetadata'
import {
  OnboardingStep,
  useOnboardingActions,
  useOnboardingData,
  useOnboardingStore,
} from '~/src/stores/useOnboardingStore'

const shoppingIntentOptions = [
  { key: 'furnishing-room-home', value: 'Furnishing a room/home' },
  { key: 'single-item', value: 'Single Item' },
  { key: 'looking-for-inspiration', value: 'Looking for inspiration' },
  { key: 'just-browsing', value: 'Just Browsing' },
]

const ShoppingIntentStep: React.FC = () => {
  const analytics = useAnalytics()
  const storedShoppingIntent = useOnboardingStore(
    (state) => state.data.onboarding_data?.shoppingIntent
  )

  const [processing, setProcessing] = useState(false)
  const [selectedIntent, setSelectedIntent] = useState<string | null>(
    storedShoppingIntent || null
  )

  const nextStep = useOnboardingActions().nextStep
  const markStepComplete = useOnboardingActions().markStepComplete
  const setShoppingIntent = useOnboardingActions().setShoppingIntent
  const onboardingData = useOnboardingData()

  // Add the useUpdateUserMetadata hook
  const { mutateAsync: updateUserMetadata } = useUpdateUserMetadata()

  const handleContinue = async (selectedIntent: string) => {
    if (processing) return

    setProcessing(true)
    setSelectedIntent(selectedIntent)

    // Track analytics
    analytics.track(AnalyticsEventName.AuthModalOnboardingSubmit, {
      step: 'shopping-intent',
      selectedIntent,
    })

    // // Save to store
    setShoppingIntent(selectedIntent)

    // Save to database
    try {
      await updateUserMetadata({
        key: 'onboarding_data',
        value: {
          ...onboardingData,
          shoppingIntent: selectedIntent,
        },
      })
    } catch (error) {
      console.error('Failed to update shopping intent:', error)
    }

    markStepComplete(OnboardingStep.SHOPPING_INTENT)
    nextStep(OnboardingStep.QUALITY_VS_VALUE)
  }

  // Update selected intent if store data changes
  useEffect(() => {
    if (storedShoppingIntent) {
      setSelectedIntent(storedShoppingIntent)
    }
  }, [storedShoppingIntent])

  return (
    <div className='space-y-8 md:space-y-10'>
      <OnboardingTitle
        title={
          <>
            Help us personalize your results.
            <br />
            What are you shopping for?
          </>
        }
        className='text-center'
        disableBack
      />
      <div className='mx-auto max-w-sm space-y-2 md:space-y-3'>
        {shoppingIntentOptions.map((option) => (
          <Button
            key={option.key}
            fullWidth
            disabled={processing && option.key !== selectedIntent}
            size='lg'
            rounded
            variant={option.key === selectedIntent ? 'primary' : 'outline'}
            className='font-medium'
            onClick={() => {
              handleContinue(option.key)
            }}
          >
            {option.value}
          </Button>
        ))}
      </div>
    </div>
  )
}

export default ShoppingIntentStep
